// Phase 4 — File upload helper.
// Wraps POST /api/uploads (multipart). Returns the structured Attachment
// dict the backend extracts.
import { supabase } from "@/lib/supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function getAccessToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/** What backend accepts (matches uploads_service.py and the file picker accept attr). */
export const ACCEPTED_MIMES = [
  "image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain", "text/markdown", "text/csv", "application/json",
];
export const ACCEPT_ATTRIBUTE = [
  ".png", ".jpg", ".jpeg", ".gif", ".webp",
  ".pdf",
  ".docx",
  ".txt", ".md", ".csv", ".json",
].join(",");

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Upload one file. Returns the Attachment dict on success, throws on error.
 * @param {File} file
 * @param {{signal?: AbortSignal, onProgress?: (pct: number) => void}} options
 */
export async function uploadFile(file, { signal } = {}) {
  if (!file) throw new Error("No file");
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 10 MB.`);
  }

  const token = await getAccessToken();
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`${API}/uploads`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
    signal,
  });

  if (!res.ok) {
    let detail = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}
